import { createFileRoute, Navigate, useNavigate, useParams } from "@tanstack/react-router";
import { workspaceSlug } from "../lib/hosts";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useWorkspace } from "../lib/workspaces";
import { useIssueByKey, ISSUE_KEY } from "../lib/issues";
import { ConfirmDialog } from "../components/workspaces/ConfirmDialog";
import { IssueDetail } from "../components/issues/IssueDetail";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { Skeleton } from "../components/ui/skeleton";
import { isNotFound, NotFound, Unavailable } from "../components/ui/states";
import { strings } from "../lib/strings";

/**
 * An issue's own page (BUILD_SPEC §10.3, §12.1), by any key it has had: a project's old key, or its own before it moved
 * to another project, finds it, and the address becomes its key as it is now.
 */
function FullIssuePage() {
  const { projectKey, issueKey } = useParams({ from: "/admin/p/$projectKey/$issueKey" });
  const slug = workspaceSlug();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const view = useWorkspace(slug);
  const found = useIssueByKey(issueKey);

  // Seed the by-id detail cache so IssueDetail's useIssue(id) is an instant hit.
  useEffect(() => {
    if (found.data) qc.setQueryData(ISSUE_KEY(found.data.issue.id), found.data);
  }, [found.data, qc]);

  const issue = found.data?.issue;
  const current = issue === undefined ? projectKey : issue.key.slice(0, issue.key.lastIndexOf("-"));
  const del = useDeleteWithUndo(issue?.projectId ?? "");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const backToBoard = () => navigate({ to: "/admin/p/$projectKey", params: { projectKey: current } });

  if (view.isLoading || found.isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-6 py-8">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!view.data) return <Navigate to="/admin" />;
  if (issue === undefined)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        {isNotFound(found.error) ? (
          <NotFound what={strings.issues.drawer.thisIssue} page />
        ) : (
          <Unavailable what={strings.issues.drawer.thisIssue} onRetry={() => void found.refetch()} page />
        )}
      </div>
    );
  // An old key, or a project's old key: the address becomes the issue's key as it is now.
  if (issue.key !== issueKey || current !== projectKey)
    return (
      <Navigate to="/admin/p/$projectKey/$issueKey" params={{ projectKey: current, issueKey: issue.key }} replace />
    );

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <button
        onClick={() => void backToBoard()}
        className="mb-2 inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {strings.issues.drawer.backToBoard}
      </button>
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <IssueDetail
          key={issue.id}
          issueId={issue.id}
          slug={slug}
          projectId={issue.projectId}
          onRequestDelete={() => setConfirmOpen(true)}
        />
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={strings.issues.drawer.deleteConfirmTitle}
        description={strings.issues.drawer.deleteConfirmBody.replace("{key}", issue.key)}
        confirmLabel={strings.issues.drawer.deleteConfirmAction}
        destructive
        busy={del.isPending}
        onConfirm={() =>
          del.requestDelete(issue.id, {
            onDeleted: () => {
              setConfirmOpen(false);
              void backToBoard();
            },
          })
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/$issueKey")({ component: FullIssuePage });
